/**
 * gateRegistry.ts — Registro unificato dei gate (A7, Fase 1)
 *
 * Solo dati e tipi. Nessun consumatore, nessun import da altri file del progetto.
 * Il registro comanda: rotte e menu verranno generati da lui nelle Fasi 3–4.
 *
 * Campi:
 *   path           — chiave primaria
 *   permission     — stringa | array OR | null (null ↔ pubblica=true oppure senzaGateVoluto≠null)
 *   label          — etichetta unica per tutti i layout
 *   icon           — nome componente Ant Design (stringa) o null
 *   gruppo         — sottomenu di appartenenza o null
 *   parent         — path padre per rotte figlie/dettaglio
 *   menu           — compare nel menu (stato OBIETTIVO, non stato attuale)
 *   esponi         — rotta registrata in App.tsx
 *   pubblica       — non richiede autenticazione
 *   senzaGateVoluto — motivazione scritta; obbligatoria se permission=null e pubblica=false
 *   gateInterni    — permessi applicati dentro la pagina (documentativo, non applicato dal registro)
 *   layout         — contenitore
 *   modulo         — 'base' ovunque (predisposizione futura)
 *   motivoNoMenu   — se menu===false, spiega perché; null se menu===true.
 *                    Testo contenente "sviluppo" = funzione non ancora implementata
 *                    (usato dalla pagina diagnostica per la sezione "IN SVILUPPO").
 *
 * Fonte: §3.1, §3.2, §3.3 di docs/design-registro-gate.md (rev. 13/08/2026)
 */

export type GateEntry = {
  path: string
  permission: string | string[] | null
  label: string
  icon: string | null
  gruppo: string | null
  parent: string | null
  menu: boolean
  esponi: boolean
  pubblica: boolean
  senzaGateVoluto: string | null
  gateInterni: string[] | null
  layout: 'selector' | 'tablet' | 'nessuno'
  modulo: string
  motivoNoMenu: string | null
}

export const gateRegistry: GateEntry[] = [

  // ── §3.1 Rotte pubbliche (pubblica: true) ──────────────────────────────────

  {
    path: '/login',
    permission: null,
    label: 'Login',
    icon: null,
    gruppo: null,
    parent: null,
    menu: false,
    esponi: true,
    pubblica: true,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'nessuno',
    modulo: 'base',
    motivoNoMenu: "rotta pubblica; non è una destinazione del menu",
  },
  {
    path: '/register',
    permission: null,
    label: 'Registrazione',
    icon: null,
    gruppo: null,
    parent: null,
    menu: false,
    esponi: true,
    pubblica: true,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'nessuno',
    modulo: 'base',
    motivoNoMenu: "rotta pubblica; non è una destinazione del menu",
  },
  {
    path: '/set-password',
    permission: null,
    label: 'Imposta password',
    icon: null,
    gruppo: null,
    parent: null,
    menu: false,
    esponi: true,
    pubblica: true,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'nessuno',
    modulo: 'base',
    motivoNoMenu: "rotta pubblica; non è una destinazione del menu",
  },
  {
    path: '/forgot-password',
    permission: null,
    label: 'Recupera password',
    icon: null,
    gruppo: null,
    parent: null,
    menu: false,
    esponi: true,
    pubblica: true,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'nessuno',
    modulo: 'base',
    motivoNoMenu: "rotta pubblica; non è una destinazione del menu",
  },
  {
    path: '/oauth/callback',
    permission: null,
    label: 'OAuth Callback',
    icon: null,
    gruppo: null,
    parent: null,
    menu: false,
    esponi: true,
    pubblica: true,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'nessuno',
    modulo: 'base',
    motivoNoMenu: "rotta pubblica di callback OAuth; non è una destinazione del menu",
  },
  {
    path: '/tire-service/checkin/:codice',
    permission: null,
    label: 'Check-in Gomme (cliente)',
    icon: null,
    gruppo: null,
    parent: null,
    menu: false,
    esponi: true,
    pubblica: true,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'nessuno',
    modulo: 'base',
    motivoNoMenu: "rotta pubblica per il cliente; non è una voce del menu operatore",
  },

  // ── §3.2 Rotte applicative (layout: 'selector') ────────────────────────────

  {
    path: '/',
    permission: null,
    label: 'LandingRedirect',
    icon: null,
    gruppo: null,
    parent: null,
    menu: false,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: "redirect intelligente alla prima rotta accessibile; non è una pagina",
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: "redirect automatico alla prima rotta accessibile; non è una pagina",
  },
  {
    path: '/dashboard',
    permission: 'dashboard.view',
    label: 'Dashboard',
    icon: 'DashboardOutlined',
    gruppo: null,
    parent: null,
    menu: true,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: null,
  },
  {
    path: '/pre-accettazione',
    permission: 'pre_accettazione.create',
    label: 'Pre-Accettazione',
    icon: 'FormOutlined',
    gruppo: null,
    parent: null,
    menu: true,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: null,
  },
  {
    path: '/customers',
    permission: 'customers.view',
    label: 'Clienti',
    icon: 'TeamOutlined',
    gruppo: 'Anagrafiche',
    parent: null,
    menu: true,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: null,
  },
  {
    path: '/vehicles',
    permission: 'vehicles.view',
    label: 'Veicoli',
    icon: 'CarOutlined',
    gruppo: 'Anagrafiche',
    parent: null,
    menu: true,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: null,
  },
  {
    path: '/suppliers',
    permission: 'suppliers.view',
    label: 'Fornitori',
    icon: 'BankOutlined',
    gruppo: 'Anagrafiche',
    parent: null,
    menu: true,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: null,
  },
  {
    path: '/officine',
    permission: 'system.manage_workshops',
    label: 'Gestione Officine',
    icon: 'ShopOutlined',
    gruppo: 'Anagrafiche',
    parent: null,
    menu: true,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: null,
  },
  {
    path: '/costi-tariffe',
    permission: 'costs.manage_rates',
    label: 'Costi e tariffe',
    icon: 'DollarOutlined',
    gruppo: 'Anagrafiche',
    parent: null,
    menu: true,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: null,
  },
  {
    path: '/rdo-templates',
    permission: 'rdo_templates.view',
    label: 'Template RDO',
    icon: 'FileTextOutlined',
    gruppo: 'Anagrafiche',
    parent: null,
    menu: true,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: null,
  },
  {
    path: '/schede-lavoro',
    permission: ['work_orders.view_own', 'work_orders.view'],
    label: 'Scheda Lavoro',
    icon: 'FileSearchOutlined',
    gruppo: null,
    parent: null,
    menu: true,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    // Il tab "Lavori in officina" vive DENTRO questa pagina e non ha una rotta
    // propria: e' un gate interno, non una destinazione. Chi non ha il permesso
    // vede la pagina esattamente come prima, senza tab.
    gateInterni: ['interventi_attivi.view'],
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: null,
  },
  {
    path: '/schede-lavoro/new',
    permission: ['work_orders.view_own', 'work_orders.view'],
    label: 'Nuova Scheda Lavoro',
    icon: null,
    gruppo: null,
    parent: '/schede-lavoro',
    menu: false,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: "form di accettazione; si apre dal pulsante Nuova Scheda nella lista. Non e' una <Route> a se': la serve /schede-lavoro/:id, dove DettaglioScheda riconosce la parola 'new' e monta la pagina classica (la V3 non ha il flusso di creazione)",
  },
  {
    path: '/schede-lavoro/:id',
    permission: ['work_orders.view_own', 'work_orders.view'],
    label: 'Dettaglio Scheda Lavoro',
    icon: null,
    gruppo: null,
    parent: '/schede-lavoro',
    menu: false,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: "rotta di dettaglio (parent: /schede-lavoro); dal 04/09/2026 e' la pagina predefinita (V3) e si apre da lista, board, dashboard, calendario",
  },
  {
    path: '/schede-lavoro/:id/classica',
    permission: ['work_orders.view_own', 'work_orders.view'],
    label: 'Dettaglio Scheda Lavoro (classica)',
    icon: null,
    gruppo: null,
    parent: '/schede-lavoro',
    menu: false,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: "rete di sicurezza: la pagina precedente resta raggiungibile dal pulsante 'Versione classica' sul dettaglio. Da rimuovere quando la V3 sara' consolidata",
  },
  {
    path: '/schede-lavoro/:id/nuova',
    permission: ['work_orders.view_own', 'work_orders.view'],
    label: 'Dettaglio Scheda Lavoro (rotta storica)',
    icon: null,
    gruppo: null,
    parent: '/schede-lavoro',
    menu: false,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: "reindirizza a /schede-lavoro/:id: era la rotta di collaudo della V3, tenuta in vita per i segnalibri e i link salvati",
  },
  {
    path: '/board-giornaliero',
    permission: 'board_giornaliero.view',
    label: 'Board Giornaliero',
    icon: 'DashboardOutlined',
    gruppo: null,
    parent: null,
    menu: true,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: null,
  },
  {
    path: '/maintenance',
    permission: 'maintenance.view',
    label: 'Manutenzioni',
    icon: 'ToolOutlined',
    gruppo: null,
    parent: null,
    menu: true,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: null,
  },
  {
    path: '/calendar',
    permission: 'calendar.view',
    label: 'Calendario',
    icon: 'CalendarOutlined',
    gruppo: null,
    parent: null,
    menu: true,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: null,
  },
  {
    path: '/rda',
    permission: 'purchase_requests.view',
    label: 'RDO (Offerte)',
    icon: 'FileTextOutlined',
    gruppo: null,
    parent: null,
    menu: true,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: null,
  },
  {
    path: '/ordini',
    permission: 'purchase_requests.receive',
    label: 'Ricezione Ordini',
    icon: 'FileSearchOutlined',
    gruppo: null,
    parent: null,
    menu: true,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: null,
  },
  {
    // menu: false — nascosta fino al completamento del cantiere RDA/RDO
    // (11/09/2026). Riaccendere quando il clic sulla riga porta al dettaglio
    // acquisto invece che alla scheda lavoro.
    path: '/acquisti',
    permission: 'purchase_requests.view',
    label: 'Acquisti',
    icon: 'InboxOutlined',
    gruppo: null,
    parent: null,
    menu: false,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: "nascosta fino al completamento del cantiere RDA/RDO — riaccendere quando il clic sulla riga porta al dettaglio acquisto invece che alla scheda lavoro",
  },
  {
    // menu: false — funzione in sviluppo (13/08/2026)
    // La pagina esiste e il permesso funziona, ma la voce menu non è ancora attiva.
    path: '/parts',
    permission: 'parts.view',
    label: 'Ricambi',
    icon: 'ToolOutlined',
    gruppo: null,
    parent: null,
    menu: false,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: "Gestione magazzino ricambi in sviluppo (13/08/2026)",
  },
  {
    // menu: false — funzione in sviluppo (13/08/2026)
    // La pagina esiste e il permesso funziona, ma la voce menu non è ancora attiva.
    path: '/tires',
    permission: 'tires.view',
    label: 'Pneumatici',
    icon: 'CarOutlined',
    gruppo: null,
    parent: null,
    menu: false,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: "Gestione pneumatici in sviluppo (13/08/2026)",
  },
  {
    path: '/courtesy-cars',
    permission: 'courtesy_cars.view',
    label: 'Anagrafica ADC',
    icon: 'CarOutlined',
    gruppo: 'Auto di Cortesia',
    parent: null,
    menu: true,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: null,
  },
  {
    path: '/courtesy-cars/planning',
    permission: 'courtesy_cars.view',
    label: 'Pianificazione',
    icon: 'CalendarOutlined',
    gruppo: 'Auto di Cortesia',
    parent: null,
    menu: true,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: null,
  },
  {
    path: '/courtesy-cars/consegna',
    permission: 'courtesy_cars.loan',
    label: 'Consegna ADC',
    icon: 'FileTextOutlined',
    gruppo: 'Auto di Cortesia',
    parent: null,
    menu: true,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: null,
  },
  {
    path: '/tire-service',
    permission: 'tire_service.view',
    label: 'Magazzino Gomme',
    icon: 'InboxOutlined',
    gruppo: 'Servizio Gomme',
    parent: null,
    menu: true,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: null,
  },
  {
    path: '/tire-checkin',
    permission: 'tire_service.checkin',
    label: 'Check-in Gomme',
    icon: 'CheckCircleOutlined',
    gruppo: 'Servizio Gomme',
    parent: null,
    menu: true,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: null,
  },
  {
    path: '/richieste-preventivo',
    permission: 'workshop_quotes.respond',
    label: 'Richieste preventivo',
    icon: 'InboxOutlined',
    gruppo: null,
    parent: null,
    menu: true,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: null,
  },
  {
    path: '/preventivi-officine',
    permission: 'workshop_quotes.view',
    label: 'Preventivi officine',
    icon: 'AuditOutlined',
    gruppo: null,
    parent: null,
    menu: true,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: null,
  },
  {
    path: '/bi',
    permission: 'analisi.view',
    label: 'Analisi',
    icon: 'BarChartOutlined',
    gruppo: null,
    parent: null,
    menu: true,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: null,
  },
  {
    path: '/consumi-ai',
    permission: 'ai_usage.view',
    label: 'Consumi AI',
    icon: 'DollarOutlined',
    gruppo: null,
    parent: null,
    menu: true,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: null,
  },
  {
    path: '/users',
    permission: 'system.manage_users',
    label: 'Gestione Utenti',
    icon: 'TeamOutlined',
    gruppo: null,
    parent: null,
    menu: true,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: null,
  },
  {
    path: '/settings',
    permission: 'system.settings',
    label: 'Impostazioni',
    icon: 'SettingOutlined',
    gruppo: 'menu utente',
    parent: null,
    menu: true,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: null,
  },
  {
    path: '/integrazioni',
    permission: 'system.settings',
    label: 'Integrazioni',
    icon: 'ApiOutlined',
    gruppo: 'menu utente',
    parent: null,
    menu: true,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: null,
  },
  {
    // Requisito di prodotto (13/08/2026): comunicazioni interne aperte a ogni utente
    // censito; le schede WhatsApp/SMS e Programmate hanno gate interni alla pagina.
    // Un gate sulla rotta escluderebbe utenti legittimi dalle interne.
    path: '/comunicazioni',
    permission: null,
    label: 'Comunicazioni',
    icon: 'MessageOutlined',
    gruppo: null,
    parent: null,
    menu: true,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: "le comunicazioni interne sono aperte a ogni utente censito; le schede esterne e programmate sono protette da gate interni alla pagina",
    gateInterni: ['comunicazioni.view', 'comunicazioni.programmati', 'comunicazioni.send'],
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: null,
  },
  {
    path: '/notifications',
    permission: null,
    label: 'Notifiche',
    icon: 'BellOutlined',
    gruppo: null,
    parent: null,
    menu: false,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: "ogni utente autenticato deve poter leggere le proprie notifiche",
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: "raggiungibile dall'icona campanella nell'header; non è una voce del menu laterale",
  },
  {
    path: '/profile',
    permission: null,
    label: 'Profilo',
    icon: 'UserOutlined',
    gruppo: 'menu utente',
    parent: null,
    menu: true,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: "ogni utente deve poter vedere e modificare il proprio profilo",
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: null,
  },
  {
    path: '/forbidden',
    permission: null,
    label: '403',
    icon: null,
    gruppo: null,
    parent: null,
    menu: false,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: "pagina di errore; proteggerla creerebbe un ciclo",
    gateInterni: null,
    layout: 'selector',
    modulo: 'base',
    motivoNoMenu: "pagina di errore di accesso; proteggerla creerebbe un ciclo",
  },

  // ── §3.3 Rotte tablet (layout: 'tablet') ──────────────────────────────────

  {
    path: '/tablet/tire-checkin',
    permission: 'tire_service.checkin',
    label: 'Tablet Check-in Gomme',
    icon: null,
    gruppo: null,
    parent: null,
    menu: true,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'tablet',
    modulo: 'base',
    motivoNoMenu: null,
  },
  {
    path: '/tablet/tire-workorder/:id',
    permission: 'tire_service.checkin',
    label: 'Tablet Scheda Gomme',
    icon: null,
    gruppo: null,
    parent: '/tablet/tire-checkin',
    menu: false,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'tablet',
    modulo: 'base',
    motivoNoMenu: "rotta di dettaglio tablet (parent: /tablet/tire-checkin); si apre dal check-in",
  },
  {
    path: '/tablet/tire-magazzino',
    permission: 'tire_service.view',
    label: 'Tablet Magazzino Gomme',
    icon: null,
    gruppo: null,
    parent: null,
    menu: true,
    esponi: true,
    pubblica: false,
    senzaGateVoluto: null,
    gateInterni: null,
    layout: 'tablet',
    modulo: 'base',
    motivoNoMenu: null,
  },
]
